import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useMemo
} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useParams } from 'react-router'
import { useVariableValue } from '@devcycle/react-client-sdk'
import {
  useCreateSubmittal,
  useFetchDocList,
  useFetchSubmittals,
  useFetchWorkflowDefs
} from '~/hooks/submittals'
import { classNames } from '~/utilities'
import { type Submittal, type ProjectDocs, ProjectDocStatus } from 'mbue-rest-client'
import { toast } from 'sonner'

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableHeader
} from '~/components/ui/Table'
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { DrawerLayout, DrawerContent, DrawerMainContent } from '~/components/DrawerLayout'
import ChangesSubmittalsTabs from '~/components/ChangesSubmittalsTabs'
import Button from '~/components/ui/Button'
import { MenuSimple as MenuSimpleIcon } from '~/components/ui/icons/MenuSimple'
import UploaderDocumentsSubmittalDialog from '~/components/Submittals/UploaderDocumentsSubmittalDialog'
import Loading from '~/components/ui/Loading'
import { ChevronUpIcon, ChevronDownIcon, LoaderCircleIcon } from 'lucide-react'
import ConfirmationDialog from '~/components/ConfirmationDialog'
import ProcesingDocuments from '~/components/Submittals/ProcesingDocuments'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/ui/DropdownMenu'
import { DownloadIcon } from 'lucide-react'
import {
  getPrecheckSubmittalList,
  type PrecheckSubmittalList
} from '~/lib/mbue-api/services/submittals'

const getColumnDefs = (
  onStartClick: () => Promise<void>,
  isCreatingSubmittal: boolean,
  navigate: (url: string) => void
): ColumnDef<Record<string, string | number>, unknown>[] => {
  return [
    {
      id: 'section',
      accessorKey: 'section_number',
      header: ({ column }) => {
        return <HeaderCell column={column}>{column.id}</HeaderCell>
      }
    },
    {
      id: 'title',
      accessorKey: 'name',
      header: ({ column }) => {
        return <HeaderCell column={column}>{column.id}</HeaderCell>
      }
    },
    {
      id: 'type',
      accessorKey: 'submittal_type',
      header: ({ column }) => {
        return <HeaderCell column={column}>{column.id}</HeaderCell>
      }
    },
    {
      id: 'action',
      accessorKey: 'docsStatus',
      header: ({ column }) => {
        return <HeaderCell column={column} notSort textAlign="center"></HeaderCell>
      },
      cell: function ActionCell(context) {
        const statusColors = {
          completed: 'bg-[#34A853] text-white hover:bg-[#34A853]/90 disabled:opacity-100',
          continue: 'bg-highlight text-content hover:bg-highlight disabled:opacity-100'
        }

        const { workflow_instance_id, is_completed } = context.cell.row.original

        const value = context.getValue() as string | number
        const variant = value === 'ready' ? 'default' : 'secondary'
        const label = value === 'ready' ? 'Start' : value

        const onClick = () => {
          if (workflow_instance_id) {
            navigate(`${window.location.pathname}/workflow/${workflow_instance_id}`)
          } else {
            onStartClick
          }
        }

        const { projectId } = useParams<{ projectId: string }>()

        const [precheckData, setPrecheckData] = useState<PrecheckSubmittalList[] | null>(
          null
        )

        useEffect(() => {
          async function fetchPrecheckData() {
            try {
              const data = await getPrecheckSubmittalList(projectId!)
              setPrecheckData(data)
            } catch (error) {
              toast.error('Precheck data not found.')
              console.error('Error fetching precheck data:', error)
            }
          }
          if (projectId) {
            void fetchPrecheckData()
          }
        }, [projectId])

        console.log('precheckData', precheckData)

        return (
          <div className="flex justify-end gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  disabled={precheckData?.length === 0 || !precheckData}
                  className="min-w-28 capitalize gap-2"
                >
                  <DownloadIcon size={16} />
                  <span>Precheck</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {precheckData?.map((item) => (
                  <DropdownMenuItem key={item.spec_section_precheck_document_name}>
                    <a
                      href={item.spec_section_precheck_document_signed_url}
                      target="_blank"
                      rel="noreferrer"
                      className="capitalize"
                    >
                      {item.spec_section_precheck_document_name.replaceAll('_', ' ')}
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant={variant}
              className={`min-w-28 capitalize ${is_completed ? statusColors.completed : statusColors.continue}`}
              onClick={onClick}
              disabled={isCreatingSubmittal}
            >
              {isCreatingSubmittal && (
                <LoaderCircleIcon className="mr-2 h-5 w-5 animate-spin" />
              )}
              {workflow_instance_id && is_completed
                ? 'Completed'
                : workflow_instance_id && !is_completed
                  ? 'Continue'
                  : label}
            </Button>
          </div>
        )
      }
    }
  ]
}

interface SubmittalTableDate extends Submittal {
  docsStatus: string
}

const mapDocListToSubmittals = (
  submittals: Submittal[],
  docList: ProjectDocs | undefined
): SubmittalTableDate[] => {
  if (!docList) {
    return submittals.map((submittal) => {
      return {
        ...submittal,
        docsStatus: 'processing'
      }
    })
  }

  return submittals.map((submittal) => {
    return {
      ...submittal,
      docsStatus:
        docList.drawing_doc.status === ProjectDocStatus.PROCESSING ||
        docList.specification_doc.status === ProjectDocStatus.PROCESSING
          ? 'processing'
          : 'ready'
    }
  })
}

const Submittals = () => {
  const asideRef = useRef<HTMLDivElement>(null)
  const [asideOpen, setAsideOpen] = useState<boolean>(true)
  const [isDocListProcessing, setIsDocListProcessing] = useState<boolean>(true)
  const [uploadDocumentsOpen, setUploadDocumentsOpen] = useState<boolean>(false)
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>()
  const isSubmittalsEnabled = useVariableValue('streamlining-submittals-enabled', true)
  const isFreeTierEnded = useVariableValue('restricted-user-accounts', false)
  const isAllowedOrgDomain = useVariableValue('allowed-organization-domains', false)
  const isRestrictedUser = isFreeTierEnded || !isAllowedOrgDomain

  const isProcore = location.pathname.startsWith('/procore') ? '/procore' : ''
  const isSubmittalsTab = location.pathname.endsWith('/submittals')

  const {
    data: submittals = [],
    isLoading: isLoadingSubmittals,
    refetch: refetchSubmittals
  } = useFetchSubmittals(
    { projectId: parseInt(projectId!, 10) },
    { enabled: isSubmittalsTab, retry: false }
  )

  const {
    data: docList,
    isLoading: isLoadingDocList,
    refetch: refetchDocList
  } = useFetchDocList(
    { projectId: parseInt(projectId!, 10) },
    {
      enabled: isSubmittalsTab,
      refetchInterval: isDocListProcessing ? 30000 : false,
      refetchIntervalInBackground: isDocListProcessing,
      retry: false
    }
  )

  const { isLoading: isLoadingWorkflowDefs, refetch: refetchWorkflowsDefs } =
    useFetchWorkflowDefs({
      enabled: false
    })

  const { mutateAsync: createSubmittal, isPending: isCreatingSubmittal } =
    useCreateSubmittal({
      onSuccess: () => {
        toast.success('Submittal created successfully')
        void refetchSubmittals()
      },
      onError: (error: Error) => {
        toast.error('Failed to create submittal')
        console.error('Error creating submittal:', error)
      }
    })

  useEffect(() => {
    if (
      docList?.drawing_doc.status === ProjectDocStatus.PROCESSING ||
      docList?.specification_doc.status === ProjectDocStatus.PROCESSING
    ) {
      setIsDocListProcessing(true)
    } else {
      setIsDocListProcessing(false)
    }
  }, [docList])

  useEffect(() => {
    if (!isSubmittalsEnabled) {
      navigate(`${isProcore}/org/${orgId}/project/${projectId}/reviews`)
    }
  }, [isSubmittalsEnabled, isProcore, orgId, projectId, navigate])

  // // Start creates a submittal using the workflow definition
  // // returned by workflow-definitions
  const onStartClick = useCallback(async () => {
    if (!projectId) {
      toast.error('Project ID is required')
      return
    }

    try {
      const { data: workflowDefs } = await refetchWorkflowsDefs()

      if (!workflowDefs || workflowDefs.length === 0) {
        toast.error('No workflow definitions found')
        return
      }

      const { definition_id } = workflowDefs[0]
      const projectIdNum = parseInt(projectId, 10)

      const createdSubmittal = await createSubmittal({
        project_id: projectIdNum,
        requestBody: {
          workflow_definition_id: definition_id
        }
      })

      if (createdSubmittal.workflow_instance_id) {
        navigate(
          `${isProcore}/org/${orgId}/project/${projectId}/submittals/workflow/${createdSubmittal.workflow_instance_id}`
        )
      }
    } catch (error: unknown) {
      console.error('Error in onStartClick:', error)
      toast.error('Failed to start submittal process')
    }
  }, [projectId, refetchWorkflowsDefs, createSubmittal, navigate, isProcore, orgId])

  const columns = useMemo(
    () =>
      getColumnDefs(onStartClick, isCreatingSubmittal, navigate) as ColumnDef<
        Submittal,
        unknown
      >[],
    [isCreatingSubmittal, navigate, onStartClick]
  )

  const tableData = useMemo(
    () => mapDocListToSubmittals(submittals, docList),
    [submittals, docList]
  )

  const table = useReactTable<Submittal>({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  })

  useEffect(() => {
    if (
      !isLoadingSubmittals &&
      docList?.drawing_doc.status === ProjectDocStatus.READY &&
      docList?.specification_doc.status === ProjectDocStatus.READY &&
      submittals.length === 0
    ) {
      void onStartClick()
    }
  }, [docList, isLoadingSubmittals, onStartClick, submittals])

  if (!isSubmittalsTab) return null

  const isLoading =
    isLoadingSubmittals ||
    isLoadingDocList ||
    isLoadingWorkflowDefs ||
    isCreatingSubmittal

  const areProcessing =
    docList?.drawing_doc.status === ProjectDocStatus.PROCESSING ||
    docList?.specification_doc.status === ProjectDocStatus.PROCESSING

  return (
    <>
      <DrawerLayout open={asideOpen} setOpen={setAsideOpen}>
        <DrawerContent className="h-full flex flex-col w-[368px]">
          <div className="flex flex-col px-6 border-b border-divider py-4">
            <div className="">
              <ChangesSubmittalsTabs defaultTab="submittals" />
            </div>
          </div>

          <div ref={asideRef} className="overflow-y-auto flex-1">
            {submittals.length > 0 && (
              <div className="px-6 flex items-center border-b border-divider min-h-20 bg-highlight-bg border-l-4 border-l-highlight">
                <h3 className="text-xl font-semibold">Division 26</h3>
              </div>
            )}
          </div>
        </DrawerContent>

        <DrawerMainContent className="w-full h-full flex flex-col">
          <div className="h-[73px] flex justify-between items-center border-b border-divider">
            <div className="flex gap-2 items-cente pl-4">
              <button
                onClick={() => setAsideOpen((prevState) => !prevState)}
                className={`border border-divider rounded-full p-[3px] ${asideOpen && 'hidden'}`}
              >
                <MenuSimpleIcon />
              </button>
            </div>
          </div>

          <div className="relative w-full flex-grow">
            {isLoading && <Loading position="absolute" />}

            {!isLoading && submittals.length === 0 && !docList && (
              <article className="h-full flex flex-col items-center justify-center gap-6">
                <p className="text-center">
                  <span className="font-bold">mbue makes submittals easy.</span> Simply
                  review the listed project <br />
                  requirements and check each box as you attach the <br />
                  necessary documentation.Follow these steps. and you&apos;ll ensure your
                  submittal is <br />
                  fully compilant and ready for approval-no guesswork, no delays. ✅
                </p>
                <Button
                  onClick={() => setUploadDocumentsOpen(true)}
                  disabled={isRestrictedUser}
                >
                  Upload Drawings & Specs
                </Button>
              </article>
            )}
            {/* TODO: Add a proper loading state */}
            {areProcessing && <ProcesingDocuments />}

            {!isLoading && !areProcessing && docList && (
              <div className="w-full px-4 py-6">
                <div className="relative w-full rounded-xl border max-h-[calc(100%_-_153px)] overflow-y-auto">
                  <Table className="min-w-full table-auto" asChild>
                    <TableHeader className="bg-gray-50 sticky top-0 z-10">
                      <TableRow>
                        {table.getHeaderGroups()[0].headers.map((header) => (
                          <TableHead key={header.id} className="py-2 px-2">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody className="bg-white divide-y divide-gray-200">
                      {table.getRowModel().rows.map((row) => {
                        return (
                          <TableRow key={row.id} className="hover:bg-gray-50">
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id} className="py-2 px-2">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </DrawerMainContent>
      </DrawerLayout>
      <UploaderDocumentsSubmittalDialog
        open={uploadDocumentsOpen}
        setOpen={setUploadDocumentsOpen}
        onDocListCreated={() => {
          void refetchDocList()
          void refetchSubmittals()
          setConfirmationDialogOpen(true)
        }}
      />
      <ConfirmationDialog
        title="All Done!"
        onConfirm={() => setConfirmationDialogOpen(false)}
        open={confirmationDialogOpen}
        onOpenChange={setConfirmationDialogOpen}
        confirmLabel="Close"
      >
        We will take it from here. You will receive an email when the submittals list is
        generated.
        <br />
        You can close your browser. get some coffee, and come back, or click
        &apos;Close&apos; to continue working on the platform while we process your
        documents.
      </ConfirmationDialog>
    </>
  )
}

export default Submittals

const HeaderCell = ({
  children,
  column,
  notSort,
  textAlign = 'left'
}: PropsWithChildren<{
  column: Column<Record<string, string | number>, unknown>
  notSort?: boolean
  textAlign?: 'center' | 'left' | 'right'
}>): JSX.Element => {
  const alignments = {
    center: 'justify-center',
    left: 'justify-start',
    right: 'justify-end'
  }
  const alignment = alignments[textAlign]
  return (
    <button
      className="w-full"
      onClick={
        notSort ? undefined : () => column.toggleSorting(column.getIsSorted() === 'asc')
      }
    >
      <div className={`flex items-center gap-1 ${alignment}`}>
        <span className="capitalize">{children}</span>
        {!notSort && (
          <div className="flex flex-col justify-center items-center">
            <ChevronUpIcon
              size={13}
              className={`${column.getIsSorted() === 'asc' ? 'text-black' : 'text-disabled'} translate-y-[2px]`}
            />
            <ChevronDownIcon
              size={13}
              className={`${column.getIsSorted() === 'desc' ? 'text-black' : 'text-disabled'} -translate-y-[2px]`}
            />
          </div>
        )}
      </div>
    </button>
  )
}

const Cell = forwardRef<HTMLDivElement, PropsWithChildren<{ className?: string }>>(
  ({ className, children }, ref) => (
    <div
      ref={ref}
      className={classNames('w-full overflow-hidden text-nowrap', className)}
    >
      {children}
    </div>
  )
)

Cell.displayName = 'Cell'
